import clsx from 'clsx';
import { Alert, Empty, Modal, Spin, Typography } from 'antd';
import { IContent, IInteractiveClass } from 'interfaces/interactive_classes';
import { formatDate } from 'services/date';
import { getAPI } from 'requests/api';
import { useCallback, useEffect, useState } from 'react';

export interface ClassRecordingsModalProps {
  interactiveClass: IInteractiveClass | null;
  open: boolean;
  onClose: () => void;
}

const getDeliveryUrl = (content: IContent): string | null =>
  content.default_version?.delivery_option?.delivery_url ?? null;

export const ClassRecordingsModal = ({
  interactiveClass,
  open,
  onClose
}: ClassRecordingsModalProps) => {
  const [contents, setContents] = useState<IContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<IContent | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);

  const classId = interactiveClass?.id;

  useEffect(() => {
    if (!open || !classId) {
      setContents([]);
      setSelected(null);
      setError(null);
      setIframeLoading(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getAPI()
      .get<IContent[]>(
        `admin/professor_area/interactive_classes/${classId}/contents`
      )
      .then(({ data }) => {
        if (cancelled) {
          return;
        }

        const list = Array.isArray(data) ? data : [];
        setContents(list);
        const firstWithUrl =
          list.find(c => Boolean(getDeliveryUrl(c))) ?? list[0] ?? null;
        setSelected(firstWithUrl);
        setIframeLoading(true);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setError('Não foi possível carregar as gravações desta aula.');
        setContents([]);
        setSelected(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, classId]);

  const handleSelectRecording = useCallback((content: IContent) => {
    setSelected(content);
    setIframeLoading(true);
  }, []);

  const deliveryUrl = selected ? getDeliveryUrl(selected) : null;

  const playableContents = contents.filter(c => getDeliveryUrl(c));
  const hasAnyUrl = playableContents.length > 0;

  return (
    <Modal
      title={
        <Typography.Title level={4} className="!m-0 !pr-8">
          {interactiveClass?.title ?? 'Gravações da aula'}
        </Typography.Title>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
      classNames={{
        content: '!h-screen !max-h-screen !rounded-none flex flex-col',
        body: '!flex-1 !min-h-0 !max-h-[calc(100vh-57px)] !p-0 overflow-hidden'
      }}
      destroyOnClose
      maskClosable
    >
      <div className="flex h-full min-h-0 flex-1 flex-col md:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-neutral-200 bg-neutral-50">
          {loading && (
            <div className="flex flex-1 items-center justify-center p-8">
              <Spin size="large" tip="Carregando gravações..." />
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-1 items-center justify-center p-6">
              <Alert
                type="error"
                message={error}
                showIcon
                className="max-w-lg"
              />
            </div>
          )}

          {!loading && !error && contents.length === 0 && (
            <div className="flex flex-1 items-center justify-center p-6">
              <Empty description="Nenhuma gravação disponível ainda para esta aula." />
            </div>
          )}

          {!loading && !error && contents.length > 0 && !hasAnyUrl && (
            <div className="flex flex-1 items-center justify-center p-6">
              <Empty description="As gravações ainda estão sendo processadas. Tente novamente em alguns minutos." />
            </div>
          )}

          {!loading && !error && deliveryUrl && (
            <div className="relative flex min-h-0 flex-1 flex-col">
              {iframeLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/85">
                  <Spin size="large" tip="Carregando player..." />
                </div>
              )}
              <iframe
                key={selected?.id ?? deliveryUrl}
                title={selected?.name ?? 'Gravação'}
                className="min-h-0 w-full flex-1 border-0"
                src={deliveryUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                onLoad={() => setIframeLoading(false)}
              />
            </div>
          )}
        </div>

        <aside className="flex w-full shrink-0 flex-col border-t border-neutral-200 bg-white md:w-[360px] md:border-l md:border-t-0">
          <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3">
            <Typography.Text strong>Gravações</Typography.Text>
            <Typography.Text type="secondary" className="ml-2">
              ({contents.length})
            </Typography.Text>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {contents.map(content => {
              const url = getDeliveryUrl(content);
              const isSelected = selected?.id === content.id;
              const created = content.default_version?.created_at;

              return (
                <button
                  key={content.id}
                  type="button"
                  disabled={!url}
                  onClick={() => url && handleSelectRecording(content)}
                  className={clsx(
                    'mb-2 w-full rounded-lg border p-3 text-left transition-colors',
                    isSelected && 'border-[#1677FF] bg-[#E6F4FF] shadow-sm',
                    !isSelected &&
                      'border-transparent bg-white hover:bg-neutral-100',
                    !url && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <Typography.Text strong className="line-clamp-2 block">
                    {content.name}
                  </Typography.Text>
                  {created && (
                    <Typography.Text
                      type="secondary"
                      className="mt-1 block text-xs"
                    >
                      {formatDate(created, 'DD/MM/YYYY HH:mm')}
                    </Typography.Text>
                  )}
                  {!url && (
                    <Typography.Text
                      type="secondary"
                      className="mt-1 block text-xs"
                    >
                      Processando...
                    </Typography.Text>
                  )}
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </Modal>
  );
};
